import { IChecklistCategory, IChecklistItem, IChecklistItemRow } from "@mctypes/checklist.types";
import { IRowMoveInfo } from "@mcapps/shared/schemas/checklistSchema";
import { openapi } from "../../../../openapi";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { ToastMessagesEnums } from "@mcapps/shared/sharedConstants";

import { sortArrayByNumber } from "../../utils";
import {
  doAddChecklistCategory,
  doAddChecklistItem,
  doGetSingleProjectChecklistParts,
  doRemoveChecklistCategory,
  doRemoveChecklistItem,
  doUpdateChecklist,
  doUpdateChecklistCategory,
  doUpdateChecklistItem,
  doUpdateChecklistRowPosition,
} from "./checklistAPI";
import { getItemCount } from "./checklistUtils";

import {
  updateChecklistProgressInRedux,
  setCurrentChecklistInRedux,
  setChecklistItemsInRedux,
  setChecklistCategoriesInRedux,
  updateChecklistInRedux,
  updateChecklistCategoryInRedux,
  updateChecklistItemsInRedux,
  deleteChecklistCategoryInRedux,
  deleteChecklistItemsInRedux,
  addChecklistCategoryInRedux,
  addChecklistItemInRedux,
  toggleTempCollapseAllCategoriesInRedux,
} from "./checklistSlice";
import { AppThunk } from "../../../store";
import { setRefactoredCollapsedCategoryIdsInRedux, setRefactoredExpandedCategoryIdsInRedux } from "./checklistSlice";

type RefactoredCategoryCollapseState = {
  allCategoriesCollapsed: boolean;
  categoryIds: string[];
  collapsedCategoryIds: Set<string>;
};

const setRefactoredCategoryCollapseState =
  (
    categoryIds: string[],
    collapsed: boolean,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    const categoryIdSet = new Set(categoryIds);

    if (collapsed) {
      dispatch(
        setRefactoredCollapsedCategoryIdsInRedux([...new Set([...refactoredCollapsedCategoryIds, ...categoryIds])]),
      );
      dispatch(
        setRefactoredExpandedCategoryIdsInRedux(refactoredExpandedCategoryIds.filter((id) => !categoryIdSet.has(id))),
      );
      return;
    }

    dispatch(setRefactoredExpandedCategoryIdsInRedux([...new Set([...refactoredExpandedCategoryIds, ...categoryIds])]));
    dispatch(
      setRefactoredCollapsedCategoryIdsInRedux(refactoredCollapsedCategoryIds.filter((id) => !categoryIdSet.has(id))),
    );
  };

export const thunkToggleRefactoredCategoryCollapse =
  (
    categoryId: string,
    collapseState: RefactoredCategoryCollapseState,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    dispatch(
      setRefactoredCategoryCollapseState(
        [categoryId],
        collapseState.collapsedCategoryIds.has(categoryId) === false,
        refactoredCollapsedCategoryIds,
        refactoredExpandedCategoryIds,
      ),
    );
  };

export const thunkToggleAllRefactoredCategoriesCollapse =
  (
    collapseState: RefactoredCategoryCollapseState,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    dispatch(
      setRefactoredCategoryCollapseState(
        collapseState.categoryIds,
        collapseState.allCategoriesCollapsed === false,
        refactoredCollapsedCategoryIds,
        refactoredExpandedCategoryIds,
      ),
    );
  };

export const getChecklistThunk =
  (checklistId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const { categories, checklist, items } = await doGetSingleProjectChecklistParts(project._id, checklistId);

    dispatch(updateChecklistProgressInRedux(checklist.progress));
    dispatch(setCurrentChecklistInRedux(checklist));
    dispatch(setChecklistItemsInRedux(sortArrayByNumber(items, "itemId")));
    dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(categories, "categoryId")));
  };

export const updateChecklistThunk =
  (
    attribute: string,
    oldValue: unknown,
    newValue: unknown,
    checklistIdOverride?: string,
    projectIdOverride?: string,
    successMessage?: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const projectId = projectIdOverride ?? project?._id;
    const checklistId = checklistIdOverride ?? checklist?._id;

    if (projectId == null) {
      throw new Error("No project selected");
    }

    if (checklistId == null) {
      throw new Error("No checklist selected");
    }

    const updatedChecklist = await doUpdateChecklist(projectId, checklistId, attribute, newValue, oldValue);

    dispatch(updateChecklistProgressInRedux(updatedChecklist.progress));
    dispatch(updateChecklistInRedux(updatedChecklist));
    if (successMessage != null) {
      dispatch(setShowSuccessMessageThunk(successMessage));
    }

    dispatch(openapi.util.invalidateTags(["Checklist"]));
  };

export const updateChecklistCategoryThunk =
  <T extends keyof IChecklistItemRow>(
    categoryId: string,
    param: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const categories = getState().checklistReducer.checklistCategories;

    const updatedChecklistCategory = await doUpdateChecklistCategory(
      project._id,
      checklist._id,
      categoryId,
      param,
      newValue,
      oldValue,
    );

    const updatedChecklistCategories: IChecklistCategory[] = sortArrayByNumber(
      categories.map((category) =>
        category._id === updatedChecklistCategory._id ? updatedChecklistCategory : category,
      ),
      "categoryId",
    );

    dispatch(updateChecklistCategoryInRedux(updatedChecklistCategories));
  };

export const updateChecklistItemThunk =
  <T extends keyof IChecklistItemRow>(
    itemId: string,
    path: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;
    const items = getState().checklistReducer.checklistItems;

    const updatedChecklistItem = await doUpdateChecklistItem(
      project._id,
      checklist._id,
      itemId,
      path,
      newValue,
      oldValue,
    );
    const updatedChecklistItems: IChecklistItem[] = sortArrayByNumber(
      items.map((category) => (category._id === updatedChecklistItem._id ? updatedChecklistItem : category)),
      "itemId",
    );

    dispatch(updateChecklistProgressInRedux(updatedChecklistItem.progress));
    dispatch(updateChecklistItemsInRedux(updatedChecklistItems));
  };

export const updateChecklistRowPositionThunk =
  (moveInfo: IRowMoveInfo): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const updatedChecklistRows = await doUpdateChecklistRowPosition(project._id, moveInfo);

    if (moveInfo.isCat) {
      dispatch(
        setChecklistCategoriesInRedux(sortArrayByNumber(updatedChecklistRows as IChecklistCategory[], "categoryId")),
      );
    } else {
      dispatch(setChecklistItemsInRedux(sortArrayByNumber(updatedChecklistRows as IChecklistItem[], "itemId")));
    }
  };

export const deleteChecklistCategoryThunk =
  (categoryId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    await doRemoveChecklistCategory(project._id, checklist._id, categoryId);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    dispatch(deleteChecklistCategoryInRedux(categoryId));
  };

export const deleteChecklistItemThunk =
  (itemId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    await doRemoveChecklistItem(project._id, checklist._id, itemId);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    dispatch(deleteChecklistItemsInRedux(itemId));
  };

export const addChecklistCategoryThunk =
  (categoryCount: number): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const checklist = getState().checklistReducer.checklist;

    const addedChecklistCategory = await doAddChecklistCategory(project._id, checklist._id, categoryCount);
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));

    dispatch(addChecklistCategoryInRedux(addedChecklistCategory));
  };

export const addChecklistItemThunk =
  (categoryId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const { checklist, checklistItems } = getState().checklistReducer;

    const itemCount = getItemCount(categoryId, checklistItems);
    const addedChecklistItem = await doAddChecklistItem(project._id, checklist._id, categoryId, itemCount);

    dispatch(addChecklistItemInRedux(addedChecklistItem));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

export const tempCollapseAllCategoriesThunk = (collapsed: boolean): AppThunk => {
  return (dispatch: any, getState) => {
    const { checklistCategories, tempHiddenCategories } = getState().checklistReducer;
    let updatedChecklistCategories: IChecklistCategory[];
    let updatedTempHiddenCategories: string[] = tempHiddenCategories;

    if (collapsed === true) {
      updatedTempHiddenCategories = checklistCategories.filter((cat) => cat.collapsed === true).map((cat) => cat._id);
      updatedChecklistCategories = checklistCategories.map((cat) => {
        const catTemp = { ...cat };
        catTemp.collapsed = true;
        return catTemp;
      });
    } else {
      updatedChecklistCategories = checklistCategories.map((cat) => {
        const catTemp = { ...cat };

        if (updatedTempHiddenCategories.includes(catTemp._id) === false) {
          catTemp.collapsed = false;
        }

        return catTemp;
      });
    }

    dispatch(
      toggleTempCollapseAllCategoriesInRedux({ cat: updatedChecklistCategories, hidden: updatedTempHiddenCategories }),
    );
  };
};
